'use client'
import React, { useEffect, useState } from 'react'
import { map, sortBy, find, compact } from 'lodash'
import { ErrorLike } from '@apollo/client'
import { useAtom } from 'jotai'
import { DateTime } from 'luxon'

import {
    DepartmentDetailFragment,
    AdminAppointmentTypeDetailFragment,
} from '@docpace/shared-graphql/fragments'
import { ctxPracticeIdAtom } from '@docpace/shared-react-atoms'
import { AdminProviderDetailFragment } from '@docpace/shared-graphql/fragments'
import { useAdminPracticeBaseProvidersQuery, useAdminPracticeBaseQuery } from '@docpace/admin-react-apollo'
import { useAppointmentCounts, useFavorites } from '@docpace/shared-react-hooks'

interface UseAdminPracticeBaseProps {
    practiceId?: string
    departmentId?: string
    providerId?: string
    skip?: boolean
}

export function useAdminPracticeBase(
    props?: UseAdminPracticeBaseProps
){
    const [fetchError, setFetchError] = useState<ErrorLike | null>(null)
    const [ ctxPracticeId ] = useAtom(ctxPracticeIdAtom)
    const practiceId = props?.practiceId ?? ctxPracticeId
    const {
        loading: practiceLoading,
        data: { practice } = {},
        refetch: practiceBaseRefetch,
        error: practiceBaseError,
    } = useAdminPracticeBaseQuery({
        variables: {
            practiceId: practiceId ?? '',
            dayStart: DateTime.now().startOf('day').toISODate(),
        },
        skip: !practiceId || !!fetchError || props?.skip,
    })
    const {
        loading: providersLoading,
        data,
        refetch: providersBaseRefetch,
        error: providersBaseError,
    } = useAdminPracticeBaseProvidersQuery({
        variables: {
            practiceId: practiceId ?? '',
            dayStart: DateTime.now().startOf('day').toISODate(),
        },
        skip: !practiceId || !!fetchError || props?.skip,
    })

    useEffect(() => {
        const error = practiceBaseError ?? providersBaseError
        if (error) setFetchError(error)
    }, [practiceBaseError, providersBaseError])
    
    const rawProviders = data?.practice?.providers

    const {
        appointmentTypes: rawAppointmentTypes,
        departments: rawDepartments,
        providerDepartmentOptions,
    } = practice ?? {}

    const { favoriteAppointmentTypeIds, favoriteDepartmentIds, favoriteProviderIds } = useFavorites()

    const { appointmentCounts: appointmentTypeAppointmentCounts } = useAppointmentCounts({ 
        groupByKey: 'appointmentTypeId'
    })

    const appointmentTypes: AdminAppointmentTypeDetailFragment[] = (compact(
        sortBy(rawAppointmentTypes?.nodes ?? [], 'nameDisplay')
    )).map((val, key)=>({
        ...val,
        isFavorited: !!favoriteAppointmentTypeIds.find(id=>val.appointmentTypeId),
        appointmentCounts: appointmentTypeAppointmentCounts.get(val.appointmentTypeId)
    }))

    const { appointmentCounts: departmentAppointmentCounts } = useAppointmentCounts({ 
        groupByKey: 'departmentId'
    })

    const departments: DepartmentDetailFragment[] = (compact(
        sortBy(rawDepartments?.nodes ?? [], 'publicName')
    )).map((val, key)=>({
        ...val,
        isFavorited: !!favoriteDepartmentIds.find(id=>val.departmentId),
        appointmentCounts: departmentAppointmentCounts.get(val.departmentId)
    }))

    const { appointmentCounts: providerAppointmentCounts } = useAppointmentCounts({ 
        groupByKey: 'providerId'
    })

    const providers: AdminProviderDetailFragment[] = (compact(
        sortBy(rawProviders?.nodes ?? [], 'displayNameCompiled')
    )).map((val, key)=>({
        ...val,
        isFavorited: !!favoriteProviderIds.find(id=>val.providerId),
        appointmentCounts: providerAppointmentCounts.get(val.providerId)
    }))

    return {
        practice: practice ?? null,
        practiceId: practiceId ?? null,
        appointmentTypes,
        appointmentTypeIds: map(
            appointmentTypes,
            ({ appointmentTypeId }) => appointmentTypeId
        ),
        appointmentTypeById: (appointmentTypeId) =>
            find(appointmentTypes, { appointmentTypeId }) ?? null,
        departments,
        departmentById: (departmentId) =>
            find(departments, { departmentId }) ?? null,
        providers,
        providerById: (providerId) => find(providers, { providerId }) ?? null,
        providerDepartmentOptions: compact(providerDepartmentOptions?.nodes),
        // isLoading: practiceLoading || jwtLoading,
        isLoading: practiceLoading || providersLoading,
        practiceBaseRefetch: async ()=>practiceBaseRefetch().then(()=>{}),
        providersBaseRefetch: async ()=>providersBaseRefetch().then(()=>{}),
    }
}

export default useAdminPracticeBase
